import moment from "moment-timezone";

const DEFAULT_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const ISO_WITH_OFFSET_RE = /(Z|[+-]\d{2}:?\d{2})$/i;
const HAS_TIME_RE = /T\d{2}:\d{2}/;

export const getResolvedTimezone = (timezone) => timezone || DEFAULT_TIMEZONE;

export const hasExplicitTime = (dateValue) =>
  typeof dateValue === "string" && HAS_TIME_RE.test(dateValue);

export const parseStoredTaskDateTime = (dateValue, timezone) => {
  if (!dateValue) return null;

  const tz = getResolvedTimezone(timezone);
  const trimmed = String(dateValue).trim();
  if (!trimmed) return null;

  let parsed;

  if (ISO_WITH_OFFSET_RE.test(trimmed)) {
    parsed = moment.parseZone(trimmed).tz(tz);
  } else if (hasExplicitTime(trimmed)) {
    parsed = moment.tz(
      trimmed,
      ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm", moment.ISO_8601],
      tz
    );
  } else {
    parsed = moment.tz(trimmed, ["YYYY-MM-DD", moment.ISO_8601], tz);
  }

  return parsed.isValid() ? parsed : null;
};

export const toUtcIsoFromLocalInput = (inputValue, timezone) => {
  if (!inputValue) return null;

  const tz = getResolvedTimezone(timezone);
  const parsed = moment.tz(
    inputValue,
    ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm", moment.ISO_8601],
    true,
    tz
  );

  return parsed.isValid() ? parsed.utc().toISOString() : null;
};

export const toLocalInputValue = (dateValue, timezone) => {
  if (!dateValue) return "";

  const trimmed = String(dateValue).trim();
  if (!trimmed) return "";

  if (!ISO_WITH_OFFSET_RE.test(trimmed)) {
    return trimmed.length >= 16 ? trimmed.slice(0, 16) : trimmed;
  }

  const parsed = parseStoredTaskDateTime(dateValue, timezone);
  return parsed ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

export const formatTaskDateTime = (dateValue, timezone) => {
  const parsed = parseStoredTaskDateTime(dateValue, timezone);
  if (!parsed) return dateValue;

  const dateStr = parsed.format("ddd, MMM D, YYYY");
  if (!hasExplicitTime(dateValue)) return dateStr;

  return `${dateStr}, ${parsed.format("h:mm A")}`;
};

export const getRelativeTaskDateInfo = (
  dateValue,
  timezone,
  { includeTime = false } = {}
) => {
  if (!dateValue) {
    return { text: "", icon: "bi-calendar3", className: "" };
  }

  const parsed = parseStoredTaskDateTime(dateValue, timezone);
  if (!parsed) {
    return { text: "", icon: "bi-calendar3", className: "" };
  }

  const tz = getResolvedTimezone(timezone);
  const today = moment().tz(tz).startOf("day");
  const tomorrow = today.clone().add(1, "day");
  const yesterday = today.clone().subtract(1, "day");
  const daysDiff = parsed.clone().startOf("day").diff(today, "days");

  const showTime = includeTime && hasExplicitTime(dateValue);
  const timeSuffix = showTime ? `, ${parsed.format("h:mm A")}` : "";

  if (parsed.isSame(today, "day")) {
    return {
      text: `Today${timeSuffix}`,
      icon: "bi-calendar-check",
      className: "due-today",
    };
  }

  if (parsed.isSame(tomorrow, "day")) {
    return {
      text: `Tomorrow${timeSuffix}`,
      icon: "bi-calendar-event",
      className: "due-tomorrow",
    };
  }

  if (parsed.isSame(yesterday, "day")) {
    return {
      text: `Yesterday${timeSuffix}`,
      icon: "bi-calendar-x",
      className: "due-overdue",
    };
  }

  if (daysDiff < 0) {
    return {
      text: `${Math.abs(daysDiff)}d overdue${timeSuffix}`,
      icon: "bi-calendar-x",
      className: "due-overdue",
    };
  }

  if (daysDiff <= 7) {
    return {
      text: `${parsed.format("ddd, MMM D")}${timeSuffix}`,
      icon: "bi-calendar-week",
      className: "due-week",
    };
  }

  return {
    text: `${parsed.format("MMM D, YYYY")}${timeSuffix}`,
    icon: "bi-calendar3",
    className: "due-later",
  };
};

export const getLocalDayRangeUtc = (timezone) => {
  const tz = getResolvedTimezone(timezone);
  const localNow = moment().tz(tz);

  return {
    startUtcIso: localNow.clone().startOf("day").utc().toISOString(),
    endUtcIso: localNow.clone().endOf("day").utc().toISOString(),
  };
};
